import { create } from '@thoughtspot/logger';
import _ from 'lodash';
import {
    AnswerKindEnum,
    recallAnswerKind,
} from '/@services/bach/spotql-answer-kind-store';
import {
    ApolloCache,
    ApolloClient,
    DocumentNode,
} from '/@src/utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';

type SessionId = {
    sessionId: string;
    genNo: number;
    acSession?: {
        sessionId: string;
        genNo: number;
    };
};

const logger = create('Stateful-Service-Hooks-Util');

/**
 * Session ID used in Jest tests (unit + integration)
 */
export const TEST_SESSION_ID: SessionId = {
    sessionId: 'TEST_SESSION_ID',
    genNo: 0,
};

export const removeTypeName = (obj: any) => {
    if (!obj) {
        return obj;
    }
    const { __typename, ...rest } = obj;
    return rest;
};

export const removeSessionTypename = (
    session: any,
    nestedSessionKeys: string[],
) => {
    nestedSessionKeys.forEach(key => {
        const nestedSessions = _.get(session, key);
        if (nestedSessions) {
            if (Array.isArray(nestedSessions)) {
                const updatedNestedSession: any = [];
                nestedSessions.forEach(nestedSession => {
                    const ns = removeTypeName(nestedSession);
                    // sessionId is null until the nested session loads
                    // (e.g. a context book whose viz has not fetched yet).
                    const sessionId = removeTypeName(ns.sessionId);
                    ns.sessionId = sessionId;
                    if (sessionId) {
                        sessionId.acSession = removeTypeName(
                            sessionId.acSession,
                        );
                    }
                    updatedNestedSession.push(ns);
                });
                _.set(session, key, updatedNestedSession);
            } else {
                const nestedSession = removeTypeName(nestedSessions);
                _.set(session, key, nestedSession);
            }
        }
    });
    return session;
};

export const removeACSessionInSpotqlSession = (
    session: any,
    sessionId: string,
) => {
    // Purely data-driven: a session is only SpotQL if something recorded that
    // kind for it. No feature-flag read here — this runs on the dispatch path
    // of every bach session op, and the load path in bach-ac-history-service
    // that sets answerKind is itself ungated, so gating only here would leave
    // the load and its follow-up ops disagreeing about acSession.
    const rememberedAnswerKind = recallAnswerKind(sessionId);
    if (rememberedAnswerKind && !session?.answerKind) {
        _.set(session, 'answerKind', rememberedAnswerKind);
    }
    if (session?.answerKind === AnswerKindEnum.Spotql) {
        _.unset(session, 'acSession');
    }
    return session;
};

export function getSession(
    client: ApolloClient<object>,
    sessionId: string,
    typename: string,
    query: DocumentNode,
    // The nested keys are used to remove the __typename field from those objects.
    nestedSessionKeys: string[] = [],
): SessionId {
    const sessionObject = {
        __typename: typename,
        sessionId,
    };
    const sessionInfoFromCache: any = client.cache.readQuery({
        id: client.cache.identify(sessionObject),
        query,
    });
    // eslint-disable-next-line no-underscore-dangle
    if (sessionId && !sessionInfoFromCache?.__typename) {
        logger.error(
            'Could not find session object in cache for typename',
            typename,
            'and sessionId',
            sessionId,
        );
    }

    const { __typename, ...session } = sessionInfoFromCache || {};
    removeSessionTypename(session, nestedSessionKeys);
    removeACSessionInSpotqlSession(session, sessionId);
    return (session as SessionId) || TEST_SESSION_ID;
}

export function getSessionFromCache(
    cache: ApolloCache<any>,
    sessionId: string,
    typename: string,
    query: DocumentNode,
    nestedSessionKeys: string[],
): SessionId {
    const sessionObject = {
        __typename: typename,
        sessionId,
    };
    const sessionInfoFromCache: any = cache.readQuery({
        id: cache.identify(sessionObject),
        query,
    });
    // eslint-disable-next-line no-underscore-dangle
    if (sessionId && !sessionInfoFromCache?.__typename) {
        logger.error(
            'Could not find session object in cache for typename',
            typename,
            'and sessionId',
            sessionId,
        );
    }

    const { __typename, ...session } = sessionInfoFromCache || {};
    removeSessionTypename(session, nestedSessionKeys);
    removeACSessionInSpotqlSession(session, sessionId);
    return (session as SessionId) || TEST_SESSION_ID;
}
